<template>
  <div v-if="!hasPermission('CanViewReturns')" class="container">
    <b-row>
      <b-col>
        <not-authorized />
      </b-col>
    </b-row>
  </div>
  <list-layout v-else>
    <template #leftSidebar>
      <ReverseLogisticsMenu />
      <br />
      <SurplusReturnTotals />
      <br />
      <ClaimsOrdersSearch :search-by-number-label="getContentItem('searchByMessageNumber')" />
    </template>

    <template #heading>
      <b-row>
        <b-col>
          <h1>Returns</h1>
        </b-col>
        <b-col sm="auto">
          <router-link
            id="startNewReturnButton"
            to="/claimsAndReturns/createReturn"
            class="btn btn-large dn-btn-large text-uppercase"
            v-if="!IsMcfs"
          >
            Start a new return
          </router-link>
        </b-col>
      </b-row>
    </template>

    <template #subTitle>
      A parts return allows you to notify DealerNet that a part was ordered in surplus, as a result of some action by
      you (the dealer), not DealerNet.
    </template>

    <template #filters>
      <b-row class="modify my-3 mx-0 align-items-center">
        <b-col md="10">
          <AvailableDealerCodeSelector
            :show-all-branches-option="true"
            class="vSelectDropDownText-disableBold"
            @selectDealerCode="setUserDealerCode"
            @initialDealerCodes="setUserDealerCode"
          />
        </b-col>
        <b-col md="2">
          <DnFunctionButton
            id="updateReturnQuery"
            :callback-fn="getReturns"
            :text="getContentItem('updateLabel')"
            :size="largeSize"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div v-if="userHasAccessToAllBranches" class="alert alert-secondary">
            {{ getContentItem("ReturnStarAccessMessage") }}
          </div>
          <div v-else-if="isAllBranchesSelected" class="alert alert-secondary">
            {{ getContentItem("ReturnAllBranchesMessage") }}
            <div v-if="totalDealersLoaded !== 0 && totalDealersLoaded !== dealerCodes.length">
              <loading-spinner />
              {{ `Returns for ${totalDealersLoaded} out of ${dealerCodes.length} dealers have been loaded.` }}
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="align-items-center">
        <b-col>
          <strong>{{ getContentItem("narrowResults") }}</strong>
        </b-col>
        <b-col class="px-1" md="3">
          <date-range-select :selected.sync="selectedDateRange" />
        </b-col>
        <b-col class="px-1" md="3">
          <v-select
            id="returnsStatus"
            v-model="selectedStatus"
            class="vSelectDropDownText disableClear"
            :options="returnStatuses"
          />
        </b-col>
        <b-col class="pl-1" md="3">
          <b-form-input v-model="filter" size="sm" :placeholder="getContentItem('search')" />
        </b-col>
      </b-row>
    </template>

    <template #table>
      <b-row class="mt-3">
        <b-col>
          <DnTable
            :items="returnsData"
            :fields="returnsDataColumns"
            :sortBy="sortBy"
            :sortDescProp="sortDesc"
            :filter="filter + selectedStatus + selectedDateRange"
            :filterFunction="filterGrid"
            :currentPage="pagination.currentPage"
            :perPage="pagination.perPage"
            :busy="isLoadingReturns"
            :showPagination="true"
            :emptyText="'No Returns Found'"
            @sort-changed="paginationReset"
            @update:currentPage="(val) => (pagination.currentPage = val)"
          >
            <template #table-busy>
              <loading-spinner class="mx-auto" />
            </template>
            <template #CustomerReferenceNumber="{ row }">
              <router-link
                v-if="row.item.ReturnStatus === 'OPEN' && row.item.Source === 'NotSet'"
                :to="'/claimsAndReturns/createReturn?returnId=' + row.item.Id"
              >
                {{ row.item.CustomerReferenceNumber }}
              </router-link>
              <router-link
                v-else
                :to="
                  '/claimsAndReturns/return/' +
                  row.item.Id +
                  '?accountId=' +
                  row.item.AccountId +
                  '&salesOrg=' +
                  row.item.SalesOrganization +
                  '&returnSource=' +
                  row.item.Source
                "
              >
                {{ row.item.CustomerReferenceNumber }}
              </router-link>
            </template>
            <template #Id="{ row }">
              <div v-if="!(row.item.ReturnStatus === 'OPEN' && row.item.Source === 'NotSet')">
                {{ row.item.Id }}
              </div>
            </template>
            <template #DateCreated="{ row }">
              <date-time :date-string="row.item.DateCreated" />
            </template>
            <template #ReturnStatus="{ row }">
              <div v-if="row.item.ReturnStatus === 'OPEN' && row.item.Source === 'NotSet'">
                <div>Saved Draft</div>
                <div>
                  <a class="noHrefLink" @click="DeleteDraft(row.item.Id)">Delete Return</a>
                </div>
              </div>
              <div v-else>
                {{ getContentItem(row.item.ReturnStatus) }}
              </div>
            </template>
          </DnTable>
        </b-col>
      </b-row>
    </template>
  </list-layout>
</template>

<script>
import vSelect from "vue-select";
import { mapGetters } from "vuex";
import ListLayout from "@/components/layouts/listLayout";
import AvailableDealerCodeSelector from "@/components/Dropdowns/AvailableDealerCodeSelector";
import { paginationMixin } from "@/components/mixins/paginationMixin";
import ReverseLogisticsMenu from "@/components/SubMenus/ReverseLogisticsMenu";
import { ReturnsService } from "@/services/ReturnsService";
import { stringHelper } from "@/components/mixins/stringHelper";
import DateTime from "@/components/Elements/DateTime";
import { sizeHelper } from "@/components/mixins/sizeHelper";
import DnFunctionButton from "@/components/Elements/DnFunctionButton";
import DateRangeSelect from "@/components/Elements/DateRangeSelect";
import ClaimsOrdersSearch from "@/components/SubMenus/ClaimsOrdersSearch";
import SurplusReturnTotals from "@/components/ReverseLogistics/Returns/SurplusReturnTotals";
import LoadingSpinner from "@/components/Elements/LoadingSpinner";
import NotAuthorized from "@/components/Elements/NotAuthorized";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

const allStatuses = "All Statuses";
export default {
  components: {
    DateRangeSelect,
    ListLayout,
    vSelect,
    AvailableDealerCodeSelector,
    ClaimsOrdersSearch,
    ReverseLogisticsMenu,
    DateTime,
    DnFunctionButton,
    SurplusReturnTotals,
    LoadingSpinner,
    NotAuthorized,
    DnTable,
  },
  mixins: [paginationMixin, stringHelper, sizeHelper],
  data: () => ({
    filter: null,
    selectedDateRange: { label: "All Dates", value: 0 },
    returnStatuses: [],
    selectedStatus: allStatuses,
    returnsDataColumns: [
      { key: "CustomerReferenceNumber", label: "Dealer Return Reference", sortable: true },
      { key: "Id", label: "Return Number", sortable: true },
      { key: "DateCreated", label: "Date Created", sortable: true },
      { key: "ReturnStatus", label: "Return Status", sortable: true },
    ],
    returnsData: [],
    sortBy: "DateCreated",
    sortDesc: true,
    selectedAccountId: 0,
    isLoadingReturns: true,
    dealerCodes: [],
    isAllBranchesSelected: false,
    userHasAccessToAllBranches: false,
    totalDealersLoaded: 0,
    includeDrafts: false,
    returnsRequestId: 0,
  }),
  computed: {
    ...mapGetters(["getContentItem", "availableDealerCodes", "user", "activeAccountId", "hasPermission", "IsMcfs"]),
  },
  mounted() {
    this.getReturns();
    this.setUserDealerCode();
  },
  methods: {
    setUserDealerCode(selectedAvailableDealerCode) {
      this.isAllBranchesSelected = false;

      if (selectedAvailableDealerCode?.value === "All Branches") {
        this.selectedAccountId = undefined;
        this.dealerCodes = this.getDealerCodesForAllBranches();
        if (this.user.DealerRange !== "*") {
          this.isAllBranchesSelected = true;
        }
      } else if (selectedAvailableDealerCode) {
        this.selectedAccountId = selectedAvailableDealerCode.AccountId;
        this.dealerCodes = [selectedAvailableDealerCode.DealerCodes.replace(/\s/g, "").split(",")];
      }
    },
    getReturns() {
      this.isLoadingReturns = true;
      this.totalDealersLoaded = 0;
      this.includeDrafts = true; // default to include drafts
      this.returnsData = [];

      // Don't load returns for "*" users browsing to "All Branches"
      if (this.user.DealerRange === "*" && (this.selectedAccountId === undefined || this.selectedAccountId === 0)) {
        this.userHasAccessToAllBranches = true;
        this.isLoadingReturns = false;
        return;
      }

      // if selected dealer is "All Branches" and not a * user, load Returns for all dealer codes
      if (!this.selectedAccountId) {
        this.dealerCodes = this.getDealerCodesForAllBranches();
        this.includeDrafts = false; // do not include drafts when "All Branches" is selected
      }

      // Fire the request for each dealer code asyncronously, and let the results trickle in.
      // Do this because sending a longer array of dealercodes causes timeouts on the backend, especially if the list of dealercodes is long.
      const requestId = ++this.returnsRequestId;
      const totalRequests = this.dealerCodes.length;
      let failedRequests = 0;
      for (var i = 0; i < totalRequests; i++) {
        if (i != 0) {
          // Prevents duplicate drafts being added after first iteration
          this.includeDrafts = false;
        }
        ReturnsService.GetReturnsSummary(this.selectedAccountId, this.user.UserId, this.includeDrafts, this.dealerCodes[i])
          .then((result) => {
            if (requestId !== this.returnsRequestId) return;
            this.pagination.totalRows += result.data.length;
            this.returnsData.push(...result.data);
            this.setReturnStatuses();
            if (result.data.length) {
              this.isLoadingReturns = false;
            }
          })
          .catch(() => {
            failedRequests++;
          })
          .finally(() => {
            if (requestId !== this.returnsRequestId) return;
            this.totalDealersLoaded++;
            if (this.totalDealersLoaded === totalRequests) {
              this.isLoadingReturns = false;
              if (failedRequests) {
                this.$store.commit("pushTimedMessage", {
                  header: "Some returns could not be loaded. Please try again.",
                  IsError: true,
                  seconds: 15,
                });
              }
            }
          });
      }
    },
    getDealerCodesForAllBranches() {
      let allBranchesDealerCodes = this.availableDealerCodes
        .map((dealerCode) => dealerCode.DealerCodes.replace(/\s/g, "").split(","));

      return allBranchesDealerCodes;
    },
    setReturnStatuses() {
      // available statuses come from the unique values of all statuses from the returns data
      this.returnStatuses = [allStatuses].concat(
        [
          ...new Set(
            this.returnsData.map((item) => {
              return item.ReturnStatus.toString();
            })
          ),
        ].sort()
      );
    },
    filterGrid(val) {
      // TODO: move this method to filterDataMixin
      if (!this.filter && !this.selectedDateRange && this.selectedStatus === allStatuses) {
        return true;
      }

      let matchesSearchFilter = true;
      let matchesDateFilter = true;
      let matchesStatusFilter = true;

      if (this.filter) {
        matchesSearchFilter =
          val.CustomerReferenceNumber.toUpperCase().includes(this.filter.toUpperCase()) ||
          val.Id.toString().includes(this.filter);
      }

      if (this.selectedDateRange.value) {
        let start = new Date();
        let itemDate = new Date(val.DateCreated);
        // difference below is in epoch so need to convert to days
        let difference = (start - itemDate) / (1000 * 60 * 60 * 24);
        matchesDateFilter = difference <= this.selectedDateRange.value;
      }

      if (this.selectedStatus !== allStatuses) {
        matchesStatusFilter = val.ReturnStatus.toString() === this.selectedStatus;
      }

      return matchesSearchFilter && matchesDateFilter && matchesStatusFilter;
    },
    DeleteDraft(id) {
      ReturnsService.DeleteDraftReturn(this.activeAccountId, this.user.UserId, id)
        .then((response) => {
          this.getReturns();
        })
        .catch(() => {
          this.$store.commit("pushTimedMessage", {
            header: "An error occurred deleting your return.",
            IsError: true,
            seconds: 15,
          });
        });
    },
  },
};
</script>